/**
 * TelegramBotWizard — drops into the Connectors page so an operator
 * can connect a real Telegram bot in under 60 seconds:
 *
 *   1. Get a bot token from @BotFather (instructions inline)
 *   2. Paste token → click "Подключить" → backend stores envelope-encrypted
 *      credentials and returns the installation_id
 *   3. Wizard shows the webhook URL + a one-line curl command that
 *      registers the bot with Telegram's setWebhook API
 *
 *  Closed-loop: as soon as the bot sees its first message, the new row
 *  appears on /intake. We poll /intake/stats once a second after install
 *  so the "Подключено!" panel updates the counter in real time.
 */

import { useEffect, useState } from "react";
import { Copy, ExternalLink, MessageSquare, ShieldCheck } from "lucide-react";
import { Badge, Button, Card, CardHeader, Input } from "@vseh/ui";

import { connectorsApi, intakeApi, type InstallationDto } from "../../shared/api";


export function TelegramBotWizard({
  existing,
  onChange,
}: {
  /** If user already has a telegram install, we render the "connected" state. */
  existing: InstallationDto | undefined;
  /** Called after a successful install/uninstall so parent refetches. */
  onChange: () => void;
}): JSX.Element {
  const [token, setToken] = useState("");
  const [secret] = useState(() => randomSecret());
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newlyInstalled, setNewlyInstalled] = useState<InstallationDto | null>(null);
  const [recentCount, setRecentCount] = useState<number | null>(null);

  const installation = newlyInstalled ?? existing ?? null;
  const installationId = installation?.id ?? "";
  const apiOrigin = window.location.origin;
  const webhookUrl = installationId
    ? `${apiOrigin}/connectors/telegram/webhook/${installationId}`
    : "";

  const setWebhookCurl = installationId && token
    ? `curl -F "url=${webhookUrl}" \\
     -F "secret_token=${secret}" \\
     https://api.telegram.org/bot${token}/setWebhook`
    : "";

  // Once we have an install, poll the telegram-channel count so the
  // wizard shows "первое сообщение пришло" the moment Telegram fires.
  useEffect(() => {
    if (!installation) return;
    let alive = true;
    const tick = async () => {
      try {
        const stats = await intakeApi.stats(24);
        if (alive) setRecentCount(stats.by_channel?.telegram ?? 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 4_000);
    return () => { alive = false; clearInterval(id); };
  }, [installation?.id]);

  const onInstall = async () => {
    setInstalling(true);
    setError(null);
    try {
      const cleanToken = token.trim();
      if (!cleanToken.match(/^\d{8,}:[A-Za-z0-9_-]{20,}$/)) {
        throw new Error("Некорректный токен. Формат: 1234567890:AAEf-...");
      }
      const result = await connectorsApi.install({
        connector_name: "telegram",
        enabled: true,
        config: { bot_token: cleanToken, webhook_secret: secret },
      });
      setNewlyInstalled(result);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setInstalling(false);
    }
  };

  if (installation && !newlyInstalled) {
    // Already-installed banner with reset option
    return (
      <Card>
        <CardHeader
          title="Telegram-бот"
          description={`Установлен · версия ${installation.connector_version}`}
          actions={
            <Badge tone={installation.enabled ? "ai" : "heritage"}>
              {installation.enabled ? "включён" : "выключен"}
            </Badge>
          }
        />
        <div className="text-sm text-base-700">
          Webhook URL:{" "}
          <code className="rounded bg-base-50 px-1.5 py-0.5 font-mono text-xs">
            {apiOrigin}/connectors/telegram/webhook/{installation.id}
          </code>
        </div>
        {recentCount !== null && (
          <div className="mt-2 text-xs text-base-500">
            За 24ч получено сообщений из Telegram: <b>{recentCount}</b>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Подключить Telegram-бот"
        description="Заявки от клиентов прямо из чатов → в воронку intake → в /orders"
        actions={
          <Badge tone="ai">
            <MessageSquare className="size-3" />
            <span className="ml-1">новое</span>
          </Badge>
        }
      />

      <ol className="space-y-4 text-sm">
        {/* Step 1 */}
        <li className="flex gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-100 font-display text-sm font-semibold text-ai-800">1</span>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-base-900">
              Получите токен у @BotFather
            </div>
            <p className="mt-1 text-base-600">
              Откройте{" "}
              <a
                href="https://t.me/BotFather"
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1 text-ai-700 underline"
              >
                @BotFather <ExternalLink className="size-3" />
              </a>{" "}
              в Telegram, отправьте <code className="rounded bg-base-50 px-1 font-mono text-xs">/newbot</code>,
              следуйте инструкциям. Получите строку вида
              {" "}<code className="rounded bg-base-50 px-1 font-mono text-xs">1234567890:AAFEf…</code>.
            </p>
          </div>
        </li>

        {/* Step 2 */}
        <li className="flex gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-100 font-display text-sm font-semibold text-ai-800">2</span>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-base-900">
              Вставьте токен — мы зашифруем и сохраним
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
              <Input
                type="text"
                placeholder="1234567890:AAFEf-Wbu-..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                disabled={installing}
              />
              <Button
                variant="ai"
                onClick={onInstall}
                loading={installing}
                disabled={!token.trim()}
              >
                Подключить
              </Button>
            </div>
            {error && (
              <div className="mt-1.5 text-xs text-danger-700">{error}</div>
            )}
            <p className="mt-1 text-2xs text-base-500">
              <ShieldCheck className="inline size-3 align-text-bottom" /> Токен
              шифруется envelope-AES перед сохранением. Раскрытие
              невозможно даже с доступом к БД.
            </p>
          </div>
        </li>

        {/* Step 3 — only after install */}
        {newlyInstalled && (
          <li className="flex gap-3">
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-100 font-display text-sm font-semibold text-ai-800">3</span>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-base-900">
                Зарегистрируйте webhook в Telegram
              </div>
              <p className="mt-1 text-base-600">
                Выполните одну команду в терминале (или Cloud Shell):
              </p>
              <CopyableBlock value={setWebhookCurl} />
              <p className="mt-2 text-2xs text-base-500">
                Готово! Когда клиент напишет боту, через 1-2 секунды
                сообщение появится на странице{" "}
                <a href="/intake" className="text-ai-700 underline">/intake</a>.
                Если уверенность ≥ 62%, появится заявка в{" "}
                <a href="/orders" className="text-ai-700 underline">/orders</a>.
              </p>
              {recentCount !== null && recentCount > 0 && (
                <div className="mt-2 rounded-md bg-ai-50/50 px-3 py-2 text-sm text-ai-800">
                  ✓ Бот получил <b>{recentCount}</b> сообщений за последние 24ч.
                </div>
              )}
            </div>
          </li>
        )}
      </ol>
    </Card>
  );
}


function CopyableBlock({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore — old browsers */ }
  };
  return (
    <div className="relative mt-2">
      <pre className="overflow-x-auto rounded-md border border-base-200 bg-base-50 p-3 pr-12 font-mono text-2xs text-base-900">
        {value}
      </pre>
      <button
        onClick={onCopy}
        className="absolute right-2 top-2 rounded-md border border-base-200 bg-surface px-2 py-1 text-2xs text-base-600 hover:bg-base-50"
        title="Скопировать"
      >
        {copied ? "✓" : <Copy className="size-3" />}
      </button>
    </div>
  );
}


function randomSecret(): string {
  // 32-char alnum, generated client-side. Telegram allows A-Z, a-z, 0-9, _ and -.
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  const buf = new Uint32Array(32);
  crypto.getRandomValues(buf);
  for (let i = 0; i < 32; i++) {
    const idx = (buf[i] ?? 0) % alphabet.length;
    out += alphabet.charAt(idx);
  }
  return out;
}
